/**
 * 고른 줄의 일괄 작업 막대 — 플레이리스트·캠페인 목록이 쓴다(화면 목록은 자기 막대가 있다).
 *
 * 한 줄씩 지우고 멈추던 일을 한 번에 한다. 고른 것 가운데 **지금 보이는 줄만** 센다 — 필터로 가려진 줄까지
 * 지우면 눈에 보이지 않는 것이 사라진다.
 */
import { useEffect, type ReactNode } from 'react';

export function SelectionBar({
  count,
  unit,
  children,
  onCancel,
  note,
}: {
  count: number;
  unit: string;
  children: ReactNode;
  onCancel: () => void;
  /** 수 바로 뒤의 덧말 — 쪽으로 나뉜 목록은 `PageSelectionNote`. */
  note?: ReactNode;
}): ReactNode {
  return (
    <div className="bulkbar">
      <span className="bulkbar-count">
        {count}
        {unit} 선택됨
      </span>
      {note}
      {children}
      <button type="button" className="link" onClick={onCancel}>
        선택 해제
      </button>
    </div>
  );
}

/** 표 머리의 "전체 선택" — 보이는 줄만 고르고 푼다. */
export function SelectAll({
  ids,
  selected,
  onChange,
}: {
  ids: readonly string[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}): ReactNode {
  const all = ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <input
      type="checkbox"
      aria-label="보이는 줄 전체 선택"
      checked={all}
      onChange={(event) => {
        const next = new Set(selected);
        for (const id of ids) {
          if (event.target.checked) next.add(id);
          else next.delete(id);
        }
        onChange(next);
      }}
    />
  );
}

/**
 * 쪽을 넘나드는 선택(2026-10-21) — 목록이 쪽으로 나뉘어도 고른 것은 쪽을 넘겨도 남는다. 지점 한 곳의 화면 300대를
 * 한 번에 재부팅하려면 그래야 한다. 대신 **안 보이는 것을 골라 두었다는 사실은 막대에 늘 적는다**(`PageSelectionNote`).
 *
 * `offPage` — 고른 것 가운데 이 쪽에 없는 수. `canSelectAll` — 이 쪽을 다 골랐는데 조건에 맞는 것이 더 있다.
 */
export function pageSelection(
  pageIds: readonly string[],
  allIds: readonly string[],
  selected: ReadonlySet<string>,
): { offPage: number; canSelectAll: boolean } {
  const onPage = new Set(pageIds);
  let offPage = 0;
  let unselected = 0;
  for (const id of allIds) {
    if (!selected.has(id)) unselected += 1;
    else if (!onPage.has(id)) offPage += 1;
  }
  const pageAll = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  return { offPage, canSelectAll: pageAll && unselected > 0 };
}

/** 선택 막대의 덧말 — "다른 쪽 3개 포함" · [조건에 맞는 125개 모두 선택]. 쪽이 하나뿐이면 아무것도 그리지 않는다. */
export function PageSelectionNote({
  pageIds,
  allIds,
  selected,
  unit,
  onChange,
}: {
  pageIds: readonly string[];
  allIds: readonly string[];
  selected: ReadonlySet<string>;
  unit: string;
  onChange: (next: Set<string>) => void;
}): ReactNode {
  const { offPage, canSelectAll } = pageSelection(pageIds, allIds, selected);
  return (
    <>
      {offPage > 0 ? (
        <span className="bulkbar-note">
          다른 쪽 {offPage.toLocaleString('ko-KR')}
          {unit} 포함
        </span>
      ) : null}
      {canSelectAll ? (
        <button type="button" className="link" onClick={() => onChange(new Set([...selected, ...allIds]))}>
          조건에 맞는 {allIds.length.toLocaleString('ko-KR')}
          {unit} 모두 선택
        </button>
      ) : null}
    </>
  );
}

/**
 * 고른 것을 `ids`(지금 조건에 맞는 줄) 안으로 줄인다 — 검색·거르기로 안 보이게 된 것이 선택에 남으면 일괄 작업이 눈에
 * 없는 것에까지 나간다. 줄이 그대로면(상태 따라가기로 다시 받기 등) 건드리지 않는다.
 */
export function useKeepSelectionWithin(
  ids: readonly string[],
  setSelected: (update: (current: Set<string>) => Set<string>) => void,
): void {
  const key = ids.join(',');
  useEffect(() => {
    const keep = new Set(key ? key.split(',') : []);
    setSelected((current) =>
      [...current].every((id) => keep.has(id)) ? current : new Set([...current].filter((id) => keep.has(id))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 줄의 내용(key)으로 본다.
  }, [key]);
}

/** 하나씩 차례로 — 서버에 일괄 경로가 없는 작업. 하나가 실패해도 나머지는 하고, 결과를 모아 돌려준다. */
export async function runEach<T extends { id: string }, R>(
  items: readonly T[],
  work: (item: T) => Promise<R>,
): Promise<{ done: T[]; results: R[] }> {
  const done: T[] = [];
  const results: R[] = [];
  for (const item of items) {
    results.push(await work(item));
    done.push(item);
  }
  return { done, results };
}
